import { PitchType } from '../../enums/pitches.enums';
import { PlateOutcome } from '../../enums/plate.enums';
import { Nullable } from '../common/i-generics';
import { IStereoVideo } from '../i-stereo-video';
import { IStrikeZone } from '../i-strike-zone';
import {
  EMPTY_SESSION_HIT_DETAILS,
  ISHHitDetails,
} from '../session-hits/i-hit-details';
import { ISpin } from './i-base';
import { IPlateLoc } from './i-plate-loc';
import { ITrajektRefBreak } from './i-trajectory';

export interface IRecapStrikezone {
  outcome: PlateOutcome;

  /** like BA_TR or SK_BL */
  grid: string;

  // 0-1 controls the horizontal position of the strike location, use -1 to hide
  grid_x: number;

  // 0-1 controls the vertical position of the strike location, use -1 to hide
  grid_z: number;

  // based on the hitter's size + machine settings
  szDef: Nullable<IStrikeZone>;

  plateFt: Nullable<IPlateLoc>;
}

export interface IRecapTargets {
  breaks: Nullable<ITrajektRefBreak>;
  plate: Nullable<IPlateLoc>;
}

export interface IPitchDetails {
  type: PitchType;
  name: string;
  speedMph: Nullable<number>;
  break: Nullable<ITrajektRefBreak>;
  spin: Nullable<ISpin>; // global frame of ref
  strikeZone: Nullable<IRecapStrikezone>;
  targets: Nullable<IRecapTargets>;
}

export interface IHitDetails extends ISHHitDetails {
  hitterName: Nullable<string>;
}

export const EMPTY_HIT_DETAILS: IHitDetails = {
  ...EMPTY_SESSION_HIT_DETAILS,
  hitterName: null,
};

// details that are primarily used by the machine
export interface IProjectorDetails {
  // for telling the machine to clear any existing recap when disabled
  clear: boolean;
  enable_pitch: boolean;
  enable_hit: boolean;
}

// sent to both the machine and web application
export interface ICommonPitchRecap {
  projector: Nullable<IProjectorDetails>;
  pitch: Nullable<IPitchDetails>;
  // not nullable so hitter name is always sent (if available) even if no hit was detected
  hit: IHitDetails;
  fireID: string;
}

// sent after a SessionHit is upserted post-detection and when a video is uploaded for a fire
export interface IPitchRecapUpdate {
  hitID?: string; // so hitting results table can fetch the hit if it doesn't already have it
  hit?: Partial<ISHHitDetails>; // we'll likely receive hit data after pitch data is initially sent, so we'll need to send it separately
  video?: IStereoVideo; // to update a specific hitting results row and/or recap widget with newly uploaded video
  hitterID?: string; // so the hitting results table can match up hitter with updated hit
  sessionID: string;
  fireID: string;
}

export const EMPTY_PITCH_DETAILS: IPitchDetails = {
  type: PitchType.None,
  name: '',
  speedMph: null,
  break: null,
  spin: null,
  strikeZone: null,
  targets: null,
};
